import * as XLSX from "xlsx"
import { importLibrary, setOptions } from "@googlemaps/js-api-loader"

// Client-side Google Maps Places search (Text Search, New). Runs entirely in the
// browser via the Maps JavaScript API's Place class - no backend/proxy needed - using
// an HTTP-referrer-restricted API key, per Google's supported browser usage pattern.
// See the "Find Prospects" setup instructions shown in the UI when no key is configured.
//
// Uses Google's official @googlemaps/js-api-loader instead of a hand-rolled <script> tag:
// a bare `<script src=".../maps/api/js?...&loading=async">` (without the internal
// `callback=google.maps.__ib__` param that Google's own bootstrap snippet adds) makes
// Google's server serve the legacy synchronous bundle instead of the one that defines
// `google.maps.importLibrary`, so importLibrary silently never appears. The loader package
// implements Google's real bootstrap logic instead of us reimplementing it by hand.
//
// Fields requested below are all "Text Search Pro" SKU fields (no rating/reviews),
// which keeps every search on the cheaper tier and inside Google's free monthly
// allowance for longer. See https://developers.google.com/maps/billing-and-pricing/pricing

const USAGE_KEY = "afb_places_usage_v1"

export interface Prospect {
  id: string
  name: string
  address: string
  phone: string
  website: string
  mapsUrl: string
  primaryType: string
}

export const MAX_RESULTS_PER_SEARCH = 20

function getApiKey(): string {
  return (import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined)?.trim() || ""
}

export function hasApiKey(): boolean {
  return getApiKey().length > 0
}

declare global {
  interface Window {
    gm_authFailure?: () => void
  }
}

// Google calls this global (if defined) when the API key is invalid, unauthorized for the
// referrer, or missing billing. When this fires, the loader's importLibrary() promise never
// resolves or rejects - it just hangs - so without this hook that failure mode is silent.
// See https://developers.google.com/maps/documentation/javascript/error-messages
window.gm_authFailure = () => {
  console.error(
    "[placesSearch] gm_authFailure: Google rejected the API key (invalid key, wrong HTTP-referrer restriction, " +
      "Places API (New) not enabled, or no billing account on the project). The search will hang - reload to retry."
  )
}

let optionsConfigured = false

function ensureOptionsConfigured(apiKey: string): void {
  if (optionsConfigured) return
  setOptions({ key: apiKey, v: "weekly" })
  optionsConfigured = true
}

function displayNameToString(displayName: string | null | undefined): string {
  return displayName || "Unnamed business"
}

export async function searchProspects(
  query: string,
  maxResultCount: number = MAX_RESULTS_PER_SEARCH
): Promise<Prospect[]> {
  const apiKey = getApiKey()
  if (!apiKey) {
    throw new Error("Missing Google Places API key")
  }

  ensureOptionsConfigured(apiKey)
  const places = await importLibrary("places")

  const request: google.maps.places.SearchByTextRequest = {
    textQuery: query,
    fields: [
      "displayName",
      "formattedAddress",
      "internationalPhoneNumber",
      "nationalPhoneNumber",
      "websiteURI",
      "businessStatus",
      "primaryType",
      "googleMapsURI",
    ],
    language: "en-US",
    region: "in",
    maxResultCount: Math.min(Math.max(1, maxResultCount), MAX_RESULTS_PER_SEARCH),
  }

  const { places: results } = await places.Place.searchByText(request)

  return (results || [])
    .filter((place) => place.businessStatus !== "CLOSED_PERMANENTLY")
    .map((place, index) => ({
      id: place.id || `${query}-${index}`,
      name: displayNameToString(place.displayName),
      address: place.formattedAddress || "",
      phone: place.internationalPhoneNumber || place.nationalPhoneNumber || "",
      website: place.websiteURI || "",
      mapsUrl: place.googleMapsURI || "",
      primaryType: place.primaryType || "",
    }))
}

function currentMonthKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${d.getMonth() + 1}`
}

function readUsage(): Record<string, number> {
  try {
    const raw = localStorage.getItem(USAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function recordSearchUsage(): number {
  const data = readUsage()
  const key = currentMonthKey()
  data[key] = (data[key] || 0) + 1
  localStorage.setItem(USAGE_KEY, JSON.stringify(data))
  return data[key]
}

export function getSearchUsageThisMonth(): number {
  return readUsage()[currentMonthKey()] || 0
}

export function exportProspectsToExcel(prospects: Prospect[]): void {
  const headers = ["Business Name", "Address", "Phone", "Website", "Type", "Google Maps Link"]
  const rows = prospects.map((p) => [p.name, p.address, p.phone, p.website, p.primaryType, p.mapsUrl])
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
  XLSX.utils.book_append_sheet(wb, ws, "Prospects")
  XLSX.writeFile(wb, `anmol-prospects-${new Date().toISOString().split("T")[0]}.xlsx`)
}
